// Version: 1.2
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { authFetch } from './auth';
import './Resources.css';

// ── UK Cyber Alerts ───────────────────────────────────────────────────────────

function CyberAlerts() {
  const [alerts, setAlerts]       = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch('/api/resources/cyber-alerts');
      const d = await r.json();
      setAlerts(d.alerts || []);
      setFetchedAt(d.fetched_at);
      setError(d.error || null);
    } catch (e) {
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await authFetch('/api/resources/cyber-alerts/refresh', { method: 'POST' });
      await load();
    } catch (e) {
      setError('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const severityClass = (s) => {
    if (!s) return '';
    const sl = s.toLowerCase();
    if (sl === 'critical' || sl === 'high') return 'sev-high';
    if (sl === 'medium') return 'sev-medium';
    return 'sev-low';
  };

  return (
    <div className="resource-panel">
      <div className="resource-panel-header">
        <div>
          <div className="resource-panel-title">UK Cyber Alerts</div>
          <div className="resource-panel-sub">
            Live feed from the NCSC · {fetchedAt ? `Updated ${formatDate(fetchedAt)}` : 'Loading…'}
          </div>
        </div>
        <button className={`btn btn-secondary btn-sm ${refreshing ? ' loading' : ''}`} onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : '↺ Refresh'}
        </button>
      </div>

      {loading && <div className="resource-loading">Loading alerts…</div>}
      {error && !loading && <div className="resource-error">{error}</div>}

      {!loading && alerts.length === 0 && !error && (
        <div className="resource-empty">No alerts available right now.</div>
      )}

      <div className="alerts-list">
        {alerts.map((alert, i) => (
          <a
            key={i}
            className="alert-card"
            href={alert.url || '#'}
            target={alert.url ? '_blank' : undefined}
            rel="noopener noreferrer"
          >
            <div className="alert-card-top">
              <div className="alert-card-title">{alert.title}</div>
              <div className="alert-card-meta">
                {alert.severity && (
                  <span className={`alert-sev ${severityClass(alert.severity)}`}>{alert.severity}</span>
                )}
                {alert.type && <span className="alert-type">{alert.type}</span>}
                <span className="alert-date">{formatDate(alert.date)}</span>
              </div>
            </div>
            {alert.summary && <div className="alert-summary">{alert.summary}</div>}
          </a>
        ))}
      </div>
    </div>
  );
}

// ── SPF/DKIM/DMARC Wizard ─────────────────────────────────────────────────────

const WIZARD_STEPS = ['Domain', 'Senders', 'DKIM', 'DMARC', 'Results'];

const SENDER_OPTIONS = [
  { id: 'm365',      label: 'Microsoft 365',  desc: 'Exchange Online / M365 mail' },
  { id: 'google',    label: 'Google Workspace', desc: 'Gmail for business' },
  { id: 'ses',       label: 'Amazon SES',      desc: 'AWS transactional email' },
  { id: 'sendgrid',  label: 'SendGrid',        desc: 'Twilio SendGrid' },
  { id: 'mailchimp', label: 'Mailchimp',       desc: 'Marketing email' },
  { id: 'custom',    label: 'Custom / Other',  desc: 'Add IPs or includes manually' },
];

function RecordBox({ label, name, type, value, note }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="record-box">
      <div className="record-box-header">
        <span className="record-label">{label}</span>
        <div className="record-dns-meta">
          <span className="record-name">{name}</span>
          <span className="record-type">{type}</span>
        </div>
        <button className="btn-copy" onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
      </div>
      <div className="record-value">{value}</div>
      {note && <div className="record-note">{note}</div>}
    </div>
  );
}

function DkimGuide({ guide }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="dkim-guide">
      <button className="dkim-guide-toggle" onClick={() => setOpen(o => !o)}>
        <span>How to set up DKIM for {guide.provider}</span>
        <span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <ol className="dkim-steps">
          {guide.steps.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
      )}
    </div>
  );
}

function EmailRecordsWizard({ prefillDomain = '' }) {
  const [step, setStep]         = useState(0);
  const [domain, setDomain]     = useState(prefillDomain);
  const [senders, setSenders]   = useState([]);
  const [customIps, setCustomIps]     = useState('');
  const [customIncludes, setCustomIncludes] = useState('');
  const [dkimSelector, setDkimSelector]     = useState('selector1');
  const [dmarcPolicy, setDmarcPolicy]       = useState('quarantine');
  const [dmarcPct, setDmarcPct]             = useState(100);
  const [dmarcRua, setDmarcRua]             = useState('');
  const [dmarcRuf, setDmarcRuf]             = useState('');
  const [spPolicy, setSpPolicy]             = useState('');
  const [results, setResults]   = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError]     = useState(null);

  // Accept prefill updates (e.g. from Fix This button)
  useEffect(() => { if (prefillDomain) setDomain(prefillDomain); }, [prefillDomain]);

  const toggleSender = (id) => {
    setSenders(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const r = await authFetch('/api/resources/generate-email-records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain,
          senders,
          custom_ips: customIps.split('\n').map(s => s.trim()).filter(Boolean),
          custom_includes: customIncludes.split('\n').map(s => s.trim()).filter(Boolean),
          dkim_selector: dkimSelector,
          dmarc_policy: dmarcPolicy,
          dmarc_pct: dmarcPct,
          dmarc_rua: dmarcRua,
          dmarc_ruf: dmarcRuf,
          dmarc_subdomain_policy: spPolicy,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setGenError(d.error || 'Generation failed'); return; }
      setResults(d);
      setStep(4);
    } catch (e) {
      setGenError('Request failed');
    } finally {
      setGenerating(false);
    }
  };

  const canNext = [
    domain.length > 2,
    senders.length > 0,
    dkimSelector.length > 0,
    true, // DMARC step — all optional
  ];

  const reset = () => {
    setStep(0); setResults(null); setSenders([]);
    setDomain(prefillDomain || ''); setDmarcRua(''); setDmarcRuf('');
    setDmarcPct(100); setDmarcPolicy('quarantine'); setSpPolicy('');
    setCustomIps(''); setCustomIncludes(''); setDkimSelector('selector1');
  };

  return (
    <div className="resource-panel">
      <div className="resource-panel-header">
        <div>
          <div className="resource-panel-title">Email Record Generator</div>
          <div className="resource-panel-sub">Generate SPF, DKIM, and DMARC DNS records for your domain</div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="wizard-steps">
        {WIZARD_STEPS.map((s, i) => (
          <div key={i} className={`wizard-step${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}>
            <div className="wizard-step-dot">{i < step ? '✓' : i + 1}</div>
            <div className="wizard-step-label">{s}</div>
          </div>
        ))}
      </div>

      {/* Step 0 — Domain */}
      {step === 0 && (
        <div className="wizard-body">
          <div className="wizard-field">
            <label className="wizard-label">Domain name</label>
            <input
              className="wizard-input"
              type="text"
              placeholder="example.co.uk"
              value={domain}
              onChange={e => setDomain(e.target.value.toLowerCase().trim())}
              autoFocus
            />
            <div className="wizard-hint">Enter the domain you send email from — the part after the @ sign.</div>
          </div>
        </div>
      )}

      {/* Step 1 — Senders */}
      {step === 1 && (
        <div className="wizard-body">
          <div className="wizard-label" style={{marginBottom: 12}}>Which services send email for <strong>{domain}</strong>?</div>
          <div className="sender-grid">
            {SENDER_OPTIONS.map(opt => (
              <button className={`btn btn-quiet sender-card${senders.includes(opt.id) ? ' selected' : ''}`}
                key={opt.id}
               
                onClick={() => toggleSender(opt.id)}
              >
                <div className="sender-card-check">{senders.includes(opt.id) ? '✓' : ''}</div>
                <div className="sender-card-label">{opt.label}</div>
                <div className="sender-card-desc">{opt.desc}</div>
              </button>
            ))}
          </div>
          {senders.includes('custom') && (
            <div className="wizard-custom-block">
              <div className="wizard-field">
                <label className="wizard-label">Custom IP addresses <span className="wizard-opt">(one per line)</span></label>
                <textarea
                  className="wizard-textarea"
                  placeholder={'192.0.2.1\n10.0.0.0/24'}
                  value={customIps}
                  onChange={e => setCustomIps(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="wizard-field">
                <label className="wizard-label">Additional include: domains <span className="wizard-opt">(one per line)</span></label>
                <textarea
                  className="wizard-textarea"
                  placeholder={'mail.example.com'}
                  value={customIncludes}
                  onChange={e => setCustomIncludes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2 — DKIM */}
      {step === 2 && (
        <div className="wizard-body">
          <div className="wizard-field">
            <label className="wizard-label">DKIM selector name</label>
            <input
              className="wizard-input"
              type="text"
              placeholder="selector1"
              value={dkimSelector}
              onChange={e => setDkimSelector(e.target.value.trim())}
            />
            <div className="wizard-hint">
              For Microsoft 365 this is usually <code>selector1</code> and <code>selector2</code>. For Google Workspace it's typically <code>google</code>. Your provider will tell you the correct value.
            </div>
          </div>
          <div className="wizard-info-box">
            <strong>Note:</strong> DKIM keys are generated by your email provider. We'll generate the DNS record name for you, but you'll need to copy the public key from your provider's admin panel and paste it into the record.
          </div>
        </div>
      )}

      {/* Step 3 — DMARC */}
      {step === 3 && (
        <div className="wizard-body">
          <div className="wizard-field">
            <label className="wizard-label">DMARC policy</label>
            <div className="policy-cards">
              <button className={`btn btn-quiet policy-card${dmarcPolicy === 'quarantine' ? ' selected' : ''}`}
               
                onClick={() => setDmarcPolicy('quarantine')}
              >
                <div className="policy-card-name">quarantine</div>
                <div className="policy-card-desc">Suspicious emails go to the spam folder. Recommended starting point.</div>
              </button>
              <button className={`btn btn-quiet policy-card${dmarcPolicy === 'reject' ? ' selected' : ''}`}
               
                onClick={() => setDmarcPolicy('reject')}
              >
                <div className="policy-card-name">reject</div>
                <div className="policy-card-desc">Failing emails are blocked outright. Use when you're confident your SPF and DKIM are correct.</div>
              </button>
            </div>
          </div>

          <div className="wizard-field">
            <label className="wizard-label">
              Percentage of mail to apply policy to
              <span className="wizard-pct-val">{dmarcPct}%</span>
            </label>
            <input
              type="range" min="1" max="100" step="1"
              value={dmarcPct}
              onChange={e => setDmarcPct(Number(e.target.value))}
              className="wizard-range"
            />
            <div className="wizard-hint">Start at 100% for full enforcement. Reduce if you want to roll out gradually.</div>
          </div>

          <div className="wizard-field">
            <label className="wizard-label">Aggregate report email <span className="wizard-opt">(rua — recommended)</span></label>
            <input
              className="wizard-input"
              type="email"
              placeholder="dmarc-reports@example.co.uk"
              value={dmarcRua}
              onChange={e => setDmarcRua(e.target.value.trim())}
            />
            <div className="wizard-hint">Receive daily XML reports showing who's sending email on behalf of your domain.</div>
          </div>

          <div className="wizard-field">
            <label className="wizard-label">Forensic report email <span className="wizard-opt">(ruf — optional)</span></label>
            <input
              className="wizard-input"
              type="email"
              placeholder="dmarc-forensic@example.co.uk"
              value={dmarcRuf}
              onChange={e => setDmarcRuf(e.target.value.trim())}
            />
          </div>

          <div className="wizard-field">
            <label className="wizard-label">Subdomain policy <span className="wizard-opt">(sp — optional)</span></label>
            <select className="wizard-select" value={spPolicy} onChange={e => setSpPolicy(e.target.value)}>
              <option value="">Inherit from main policy</option>
              <option value="none">none — no action on subdomains</option>
              <option value="quarantine">quarantine</option>
              <option value="reject">reject</option>
            </select>
          </div>

          {genError && <div className="wizard-error">{genError}</div>}
        </div>
      )}

      {/* Step 4 — Results */}
      {step === 4 && results && (
        <div className="wizard-body">
          <div className="results-intro">
            Add these three DNS records to <strong>{results.domain}</strong>. Changes typically take 24–48 hours to propagate.
          </div>

          <RecordBox
            label="SPF"
            name={results.spf.name}
            type={results.spf.type}
            value={results.spf.value}
            note={results.spf.note}
          />

          <RecordBox
            label="DKIM"
            name={results.dkim.name}
            type={results.dkim.type}
            value={results.dkim.value}
            note={results.dkim.note}
          />

          {results.dkim_guides && results.dkim_guides.map((g, i) => (
            <DkimGuide key={i} guide={g} />
          ))}

          <RecordBox
            label="DMARC"
            name={results.dmarc.name}
            type={results.dmarc.type}
            value={results.dmarc.value}
            note={results.dmarc.note}
          />

          <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={reset}>← Start over</button>
        </div>
      )}

      {/* Wizard nav */}
      {step < 4 && (
        <div className="wizard-nav">
          {step > 0 && (
            <button className="btn btn-secondary" onClick={() => setStep(s => s - 1)}>← Back</button>
          )}
          <div style={{flex:1}} />
          {step < 3 ? (
            <button className="btn btn-primary"
             
              disabled={!canNext[step]}
              onClick={() => setStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button className="btn btn-primary"
             
              disabled={generating}
              onClick={generate}
            >
              {generating ? 'Generating…' : 'Generate Records'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Resources page ─────────────────────────────────────────────────────────────

const TABS = [
  { id: 'alerts',  label: 'UK Cyber Alerts', icon: '⚠' },
  { id: 'email',   label: 'Email Records',   icon: '✉' },
];

export default function Resources() {
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const initTab  = params.get('tab') || 'alerts';
  const prefillDomain = params.get('domain') || '';

  const [activeTab, setActiveTab] = useState(initTab);

  // Support deep-linking from Fix This buttons
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get('tab');
    if (t) setActiveTab(t);
  }, [location.search]);

  return (
    <div className="resources-page">
      <div className="page-header">
        <div className="page-title">Resources</div>
        <div className="page-subtitle">Guidance, tools, and live intelligence for your security posture</div>
      </div>

      <div className="resources-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`resources-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="resources-content">
        {activeTab === 'alerts' && <CyberAlerts />}
        {activeTab === 'email'  && <EmailRecordsWizard prefillDomain={prefillDomain} />}
      </div>
    </div>
  );
}
